import React, {useMemo, useState, useCallback} from 'react';
import styled, {css} from 'styled-components';
import {useHistory, Link} from 'react-router-dom';
import {H1} from '../../../components/Typography/next';

import Breadcrumbs from '../../../components/NavBar/components/Breadcrumbs';

import {useUserDataContext} from '../../../contexts/UserData';
import {useBootcampConfigContext} from '../../../contexts/BootcampConfig';
import {useAsync} from '@bootcamp/shared/src/util/hooks';
import {capitalize, getScore} from '@bootcamp/shared/src/util';
import {getTestProgressByUserIdHashTestId} from '@bootcamp/shared/src/requests';
import moment from 'moment';
import {Helmet} from 'react-helmet';
import {Edit} from '@styled-icons/material-rounded/Edit';
import Storage from '@aws-amplify/storage';

import {CellProgressBar} from '../../components/Performance/shared';
import {ChevronRight} from '@styled-icons/fluentui-system-filled/ChevronRight';
import {PauseCircle} from '@styled-icons/fluentui-system-filled/PauseCircle';
import {Blurb} from '../../components/Performance/shared';
import {Info} from '@styled-icons/fluentui-system-filled/Info';
import Container, {Wrapper} from '../PageContainer';
import { Label2, Label3 } from '../../../components/Typography/next';

import Table from '@bootcamp/web/src/bootcamps/components/PreviousTestsTable';

import {theme} from '@bootcamp/shared/src/styles/theme';

const StyledContainer = styled(Container)`
  ${Wrapper} {
    overflow: hidden;
  }
`;
const StyledBreadcrumbs = styled(Breadcrumbs)`
  margin-bottom: ${({theme}) => theme.layouts.spacing.s};
`;
const Title = styled(H1)`
  color: white;
  margin-bottom: 16px;
`;
const StyledBlurb = styled(Blurb)`
  margin-bottom: 16px;
`;
const StyledLink = styled(Link)`
  color: white;
`;
const PauseIcon = styled(PauseCircle)`
  width: 14px;
  height: 14px;
  fill: ${({theme}) => theme.colors.interfacePalette.yellow.default};
`;
export const CellWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const StyledChevronRight = styled(ChevronRight)`
  color: ${({theme}) => theme.colors.typographyPalette.tertiary};
`;
const Chiclet = styled(Label3)`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 8px;
  width: auto;
  font-weight: ${({type}) => type === 'paused' ? 400 : 600};
  gap: 4px;
  ${({theme, type}) => type === 'learning'
    ? css`background: ${theme.colors.interfacePalette.red.light}; color: ${theme.colors.interfacePalette.red.dark};`
    : type === 'reviewing'
    ? css`background: ${theme.colors.interfacePalette.yellow.light}; color: ${theme.colors.interfacePalette.yellow.dark};`
    : type === 'mastered'
    ? css`background: ${theme.colors.interfacePalette.green.light}; color: ${theme.colors.interfacePalette.green.dark};`
    : type === 'paused'
    ? css`border: 1px solid ${theme.colors.interfacePalette.yellow.default}; color: ${theme.colors.interfacePalette.yellow.dark};`
    : type === 'bookmarked'
    ? css`background: ${theme.colors.brandPalette.blue.light}; color: ${theme.colors.brandPalette.blue.dark};`
    : css`background: ${theme.colors.neutralsPalette.offWhite}; color: ${theme.colors.neutralsPalette.grey};`
  };
`;
export const AvgScoreCell = styled.div`
  color: ${({theme}) => theme.colors.typographyPalette.secondary};
`;
const PausedChiclet = () => {
  return (
    <CellWrapper>
      <Chiclet type={'paused'}>
        <PauseIcon/>
        Paused
      </Chiclet>
    </CellWrapper>
  );
};
const ScoreCellText = styled(Label2)`
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  width: 42px;
`;
export const ScoreCell = ({score, avgScore, maxScore, hideBar}) => {
  const segments = [
    {count: score, color: theme.colors.brandPalette.blue.gradient},
  ];
  const scorePercentage = Math.round((score / maxScore) * 100) || 0;
  const markPosition = parseInt(avgScore.split('%')[0]);

  return (
    <CellWrapper>
      <ScoreCellText>{scorePercentage}%</ScoreCellText>
      {!hideBar && <CellProgressBar segments={segments} total={maxScore} markPosition={markPosition} width={'70px'}/>}
    </CellWrapper>
  );
};

function getScoreValues(questionProgresses, overridingMaxScore) {
  const {score, maxScore} = questionProgresses.reduce((acc, curr) => {
    const answerState = JSON.parse(curr?.answerState || "{}");
    // TODO we'll probably have to pull max score out of the question like we're doing on the score report page since
    // it isn't always in answer state...
    return {
      score: acc.score + (answerState.score || (curr.didSelectCorrectAnswer && 1) || 0),
      maxScore: acc.maxScore + (answerState.maxScore || 1)
    }
  }, {score: 0, maxScore: 0});

  return {score, maxScore: overridingMaxScore || maxScore};
}

function getAvgScorePercentage(questionProgresses, bootcamp) {
  try {
    const {score, total} = questionProgresses.reduce((acc, curr) => {
      const {maxScore} = getScore(bootcamp, curr) || 1;
      const statistic = JSON.parse(curr?.question?.statistics || "{}");
      // TODO we'll probably have to pull max score out of the question like we're doing on the score report page since
      // it isn't always in answer state...

      return {
        score: acc.score + statistic.summedScore,
        total: acc.total + (statistic.totalRecords * maxScore)
      }
    }, {score: 0, total: 0});


    return `${Math.floor(((score / total) || 0) * 100)}%`;
  } catch (error) {
    return '49%';
  }
}

const retrieveQuestionProgresses = testData => {
  const {blockProgresses: {items: blockProgressItems}} = testData || {blockProgresses: {items: []}};
  return blockProgressItems.reduce((acc, blockProgress) => [...acc, ...(blockProgress.questionProgresses.items || [])], []);
}

const PreviousTests = () => {
  const [loading, setLoading] = useState(true);
  const {DEFAULT_USER_ID, searchUserInteractions, bootcamp} = useUserDataContext();
  const [currentPage, setCurrentPage] = useState({pageIndex: 0, pageSize: 10});
  const savedTests = searchUserInteractions(`${DEFAULT_USER_ID}_${bootcamp}_savedTests`);

  const fetchSavedTests = useCallback(async () => {
    if (!DEFAULT_USER_ID) return;

    setLoading(true);

    const testList = savedTests?.testList || [];


    const result = await Promise.all(testList?.slice()?.reverse()?.slice(currentPage.pageIndex*currentPage.pageSize, (currentPage.pageIndex + 1) * currentPage.pageSize)?.map(async testStorageKey => {
      try {
        const result = await Storage.get(testStorageKey, {
          download: true,
          cacheControl: 'no-cache',
          contentType: 'application/json',
        })
        const testData = await new Response(result.Body).json();
        const testResults = testData.testId ? await getTestProgressByUserIdHashTestId(`${DEFAULT_USER_ID}#${testData.testId}`, true) : {data: {TestProgressByTestId: {items: []}}};
        return {testData, testResults}
      } catch (e) {
        console.log(e);
      }
    }));

    setLoading(false);

    return result;
  }, [DEFAULT_USER_ID, currentPage]);

  const {value, execute} = useAsync(fetchSavedTests);
  const placeholderData = useMemo(() => {
    return Array.from({ length: currentPage.pageSize }, (_, index) => ({
      testData: {
        config: {
          customTitle: `Placeholder Test #${index + 1}`,
          masteryLevel: ['learning', 'reviewing', 'mastered', 'untagged', 'bookmarked'][index % 5],
          createdAt: new Date().toISOString(),
          maxScore: 100,
          submitted: false,
        },
        selectedQuestions: Array.from({ length: 10 }),
      },
      testResults: {
        data: {
          TestProgressByTestId: {
            items: [],
          },
        },
      },
    }));
  }, [currentPage.pageSize]);

  const hasResults = savedTests?.testList?.length > 0;
  const data = value?.length ? value : placeholderData;

  const columns = useMemo(
    () => {
      let columns = [
        {
          Header: '#',
          Cell: ({row}) => (savedTests?.testList?.length - row.index - (currentPage.pageIndex*currentPage.pageSize) || row.index + 1),
          accessor: row => savedTests?.testList?.length - row.index - (currentPage.pageIndex*currentPage.pageSize)
        },
        {
          Header: 'Test Name',
          Cell: ({row}) => {
            const {testTitle, testStorageKey, customTitle} = row.original.testData.config;
            const testName = `Previous Test #${savedTests?.testList?.length - row.index - (currentPage.pageIndex*currentPage.pageSize)}`
            return (
              <div>{customTitle || testName} <Edit className="editButton" style={{cursor: 'pointer'}} size={16} onClick={async e => {
                e.persist();
                e.preventDefault();
                e.stopPropagation();
                const newTitle = window.prompt('Rename test to...');
                if (!newTitle) return;
                const testData = row.original.testData;
                testData.config.customTitle = newTitle;
                await Storage.put(testStorageKey, JSON.stringify(testData), {contentType: 'application/json'});
                execute()
                return false;
              }} /></div>
            )
          }
        },
        {
          Header: 'Question Pool',
          Cell: ({row}) => {
            const type = row.original.testData.config.masteryLevel;
            return (
              <CellWrapper>
                <Chiclet type={type}>{type === 'untagged' ? 'Unused' : capitalize(type)}</Chiclet>
              </CellWrapper>
            );
          }
        },
        {
          Header: 'Date Taken',
          Cell: ({row}) => {
            const {createdAt} = row.original.testData.config;
            return moment(createdAt).format('ll');
          },
          accessor: row => new Date(row.createdAt),
          sortType: 'datetime'
        },
        {
          Header: 'Score',
          Cell: ({row}) => {
            const {data: {TestProgressByTestId: {items}}} = row.original.testResults;
            const {config: {maxScore}, submitted} = row.original.testData || {};

            if (items[0]) {
              const questionProgresses = retrieveQuestionProgresses(items[0]);
              const {score, maxScore: max} = getScoreValues(questionProgresses, maxScore);
              const avgScore = getAvgScorePercentage(questionProgresses, bootcamp);

              return submitted
                ? <ScoreCell score={score} avgScore={avgScore} maxScore={max}/>
                : <PausedChiclet/>;
            }
            return 'N/A'
          },
        },
        {
          Header: 'Avg. Score',
          meta: {
            style: { color: theme.colors.typographyPalette.secondary },
          },
          Cell: ({row}) => {
            const {data: {TestProgressByTestId: {items}}} = row.original.testResults;
            const {config: {maxScore}, submitted} = row.original.testData || {};

            const questionProgresses = retrieveQuestionProgresses(items[0]);

            return (
              <AvgScoreCell>
                {submitted ? getAvgScorePercentage(questionProgresses, bootcamp) : null}
              </AvgScoreCell>
            );
          },
        },
        {
          Header: 'Questions',
          Cell: ({row}) => {
            return row.original.testData.selectedQuestions.length
          }
        },
        {
          Header: ' ',
          Cell: () => <StyledChevronRight size={16}/>
        }
      ]
      return columns
    },
    [data, execute]
  );

  const {config} = useBootcampConfigContext();
  const history = useHistory();


  function handleNextPage() {
    setCurrentPage(currentPage => ({...currentPage, pageIndex: currentPage.pageIndex + 1}));
  }

  function handlePreviousPage() {
    setCurrentPage(currentPage => ({...currentPage, pageIndex: currentPage.pageIndex - 1}));
  }

  function handlePageSize(pageSize) {
    setCurrentPage(currentPage => ({pageIndex: 0, pageSize}));
  }

  return (
    <StyledContainer>
      <Helmet>
        <title>{config.meta.siteTitle} | Previous Tests</title>
        <meta name="description" content={`${config.meta.siteTitle} Previous Tests`}></meta>
      </Helmet>
      <StyledBreadcrumbs history={history} lockAtDepth={2} />
      <Title>Previous Tests</Title>
      {bootcamp === 'nclex' &&
        <StyledBlurb lightMode>
          Your Readiness Exam attempts can be reviewed on the <StyledLink to={'/nclex/next-gen-readiness-exams'}>Readiness Exams page.</StyledLink>
        </StyledBlurb>
      }
      {bootcamp === 'med-school' &&
        <StyledBlurb lightMode>
          Your Self Assessment attempts can be reviewed on the <StyledLink to={'/med-school/self-assessment'}>Self Assessment page.</StyledLink>
        </StyledBlurb>
      }
      {bootcamp === 'inbde' &&
        <StyledBlurb lightMode>
          Your Simulation Exam attempts can be reviewed on the <StyledLink to={'/inbde/simulation-exam'}>Simulation Exams page.</StyledLink>
        </StyledBlurb>
      }
      <Table
        columns={columns}
        data={data}
        placeholderData={placeholderData}
        loading={loading}
        pageCount={Math.ceil(savedTests?.testList?.length / currentPage.pageSize)}
        currentPage={currentPage}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        onPageSize={handlePageSize}
        hasResults={hasResults}
        />
    </StyledContainer>
  )
};


export default PreviousTests;